import React, { useCallback, useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import "./TrailerModal.css";
import Icon from "./Icon";

const CLOSE_ANIMATION_MS = 220;

/** @typedef {import("../types").Video} Video */

/**
 * Modal con el reproductor de trailer. Encapsula su propio ciclo de vida
 * (animación de cierre, timer, atajo de teclado Escape y foco) para que
 * HeroSection no tenga que conocer estos detalles.
 * @param {{ trailer: Video, title: string, onClose: () => void }} props
 */
export default function TrailerModal({ trailer, title, onClose }) {
  const [isClosing, setIsClosing] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const closeTimerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const handleClose = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [isClosing, onClose]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleEscapeKey);
    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [handleClose]);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    return () => {
      if (previouslyFocusedRef.current instanceof HTMLElement) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, []);

  if (!trailer) return null;

  return (
    <div
      className={`trailer-modal ${isClosing ? "closing" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={`Trailer de ${title}`}
      onClick={handleClose}
    >
      <div className="trailer-modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="trailer-modal-header">
          <div>
            <p className="trailer-eyebrow">Ahora viendo</p>
            <h3>{title}</h3>
          </div>
          <button
            ref={closeButtonRef}
            onClick={handleClose}
            className="trailer-close"
            type="button"
            aria-label="Cerrar trailer"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="video-modal-container">
          {!isPlayerReady ? (
            <div className="video-loading" aria-hidden="true">
              <span className="video-spinner" />
            </div>
          ) : null}
          <YouTube
            videoId={trailer.key}
            className={`video-frame ${isPlayerReady ? "is-ready" : ""}`}
            containerClassName="video-frame-container"
            onReady={() => setIsPlayerReady(true)}
            opts={{
              width: "100%",
              height: "100%",
              playerVars: {
                autoplay: 1,
                controls: 1,
                modestbranding: 1,
                rel: 0,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}
